import { resolvePathFromSegments, resolveResourceFromConfig } from '@/support';
import DirectoryResource from '@structure/Resources/Directory';
import { AppState, SerializedResource } from '@/types';
import AbstractResource from '@structure/Resources/AbstractResource';
import Directory from '@structure/Resources/Directory';

// Find config object for the requested path in the JSON structure
const fetchConfigFromStructure = (
  needle: string,
  haystack: SerializedResource
): SerializedResource | undefined => {
  if (!needle || !haystack.directory) {
    return undefined;
  }

  const comparo = resolvePathFromSegments(
    haystack.directory,
    haystack.basename
  );

  if (needle === comparo) {
    return haystack;
  }

  const children = haystack.children || [];

  for (const child of children) {
    const result = fetchConfigFromStructure(needle, child);

    if (result) {
      return result;
    }
  }

  return undefined;
};

export const currentResource = (state: AppState): AbstractResource | null => {
  if (!state.path || !state.structure) {
    // either the 'needle' or 'haystack' in the resource resolution is missing
    return null;
  }

  const resourceConfig = fetchConfigFromStructure(state.path, state.structure);

  if (!resourceConfig) {
    return null;
  }

  // try to get the parent directory from the JSON structure
  const parentConfig = fetchConfigFromStructure(
    resourceConfig.directory,
    state.structure
  );

  if (parentConfig !== undefined) {
    return resolveResourceFromConfig(
      resourceConfig,
      new DirectoryResource(parentConfig)
    );
  }

  // otherwise the config is expected to be the root directory (no parent found in
  // the structure). If the config is NOT for a directory, the instantiation of
  // the below class instance will throw an Error.
  return new Directory(resourceConfig);
};

export const currentDirectory = (state: AppState): DirectoryResource | null => {
  const resource = currentResource(state);

  if (resource === null) {
    return null;
  } else if (resource instanceof DirectoryResource) {
    return resource;
  } else {
    return resource.parent();
  }
};
